import { useIsVoiceAssistantEnabled } from '@/features/flags';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { AudioLines } from 'lucide-react';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { voiceAssistantMessages as messages } from '@heidi/agents';
import { useIsAuthenticated } from '@heidi/agents/data';
import { useVoiceAssistant } from '../hooks/useVoiceAssistant';

interface VoiceAssistantTriggerProps {
  className?: string;
  /** Button size — `lg` for placements beside taller input bars. */
  size?: 'md' | 'lg';
}

/**
 * Entry point for the realtime voice assistant in composer toolbars. Self-gating:
 * renders nothing unless the feature flag is on and the user is authenticated.
 * Acts as a toggle — starts a session when idle, ends the live session when one
 * is running.
 */
export function VoiceAssistantTrigger({
  className,
  size = 'md',
}: VoiceAssistantTriggerProps) {
  const intl = useIntl();
  const isEnabled = useIsVoiceAssistantEnabled();
  const isAuthenticated = useIsAuthenticated();
  const { isOpen, open, close } = useVoiceAssistant();
  const [tooltipOpen, setTooltipOpen] = useState(false);

  if (!isEnabled || !isAuthenticated) return null;

  const ariaLabel = isOpen
    ? intl.formatMessage(messages.endSession)
    : intl.formatMessage(messages.startSession);
  const tooltipLabel = isOpen
    ? intl.formatMessage(messages.endSession)
    : intl.formatMessage(messages.triggerTooltip);

  return (
    <Tooltip open={isOpen ? false : tooltipOpen} onOpenChange={setTooltipOpen}>
      <TooltipTrigger asChild>
        <Button
          variant="secondary"
          size={size}
          iconOnly
          aria-label={ariaLabel}
          aria-pressed={isOpen}
          onClick={() => {
            setTooltipOpen(false);
            if (isOpen) close();
            else open();
          }}
          className={cn(
            'shrink-0 rounded-lg transition-colors duration-150 ease-out active:scale-95',
            size === 'lg' ? '[&_svg]:size-6' : '[&_svg]:size-5',
            className
          )}
        >
          <AudioLines aria-hidden />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {tooltipLabel}
        <TooltipArrow />
      </TooltipContent>
    </Tooltip>
  );
}
